import { useId } from 'react'
import { FaInfoCircle } from 'react-icons/fa'
import { ViewTitle } from '../../reusable'
import { Spinner } from '../../ui/spinner'
import { IneligibleFundingClientsMetricCard } from '../../ui/ineligibleFundingClients/metricCard/IneligibleFundingClientsMetricCard'
import efx from '../ineligibleFundingClientsView/styles.module.scss'
import styles from './styles.module.scss'
import { GAP_PAGE_SIZE, GAP_SOURCE_OPTIONS, useIntroducerFundingGapViewState } from './useIntroducerFundingGapViewState'

export const IntroducerFundingGapView = () => {
    const {
        loading,
        error,
        summary,
        search,
        setSearch,
        selectedSources,
        selectedLenders,
        openGapFilter,
        filterPopoverPosition,
        gapFilterBarRef,
        sorted,
        sourceCounts,
        lenderOptions,
        lenderCounts,
        lenderSort,
        setLenderSort,
        filteredRows,
        paginatedRows,
        currentPage,
        totalPages,
        handlePageChange,
        gapDistinctEmails,
        loadedGapDistinctEmails,
        gapPctOfTableRows,
        distinctEmailsPctOfLoadedGap,
        hasSearch,
        hasSourceFilter,
        hasLenderFilter,
        searchSummary,
        sourceFilterSummary,
        lenderFilterSummary,
        toggleGapFilter,
        toggleSource,
        resetSourceFilter,
        toggleLender,
        resetLenderFilter,
        hasCfaFilter,
        hasEsignFilter,
        cfaFilterSummary,
        esignFilterSummary,
        cfaFilter,
        setCfaFilter,
        esignFilter,
        setEsignFilter,
        resetCfaFilter,
        resetEsignFilter,
        hasTelFilter,
        hasDobFilter,
        hasAddressFilter,
        telFilterSummary,
        dobFilterSummary,
        addressFilterSummary,
        telFilter,
        setTelFilter,
        dobFilter,
        setDobFilter,
        addressFilter,
        setAddressFilter,
        resetTelFilter,
        resetDobFilter,
        resetAddressFilter,
        exportGapTableCsv,
    } = useIntroducerFundingGapViewState()

    const scanTotalsTooltipId = useId()
    const exportDisabled = loading || !!error || filteredRows.length === 0
    const nonMatchedRaw =
        summary !== null ? summary.introducerClaimsScanned - summary.matchedToFundingMonitor : 0

    return (
        <div className={styles.wrap}>
            <ViewTitle
                text={'Introducer claims (not in funding batches)'}
                button={
                    <div className={styles.titleActions}>
                        {summary !== null && !error && (
                            <span className={styles.scanTotalsWrap}>
                                <button
                                    type="button"
                                    className={styles.scanTotalsInfoBtn}
                                    aria-describedby={scanTotalsTooltipId}
                                    aria-label="Introducer scan totals"
                                >
                                    <FaInfoCircle className={styles.scanTotalsIcon} aria-hidden />
                                </button>
                                <span id={scanTotalsTooltipId} role="tooltip" className={styles.scanTotalsTooltip}>
                                    <span className={styles.scanTotalsTooltipTitle}>Introducer scan totals</span>
                                    <ul>
                                        <li>
                                            Claims scanned across introducer DBs:{' '}
                                            <strong>{summary.introducerClaimsScanned.toLocaleString()}</strong>
                                        </li>
                                        <li>
                                            Matched to a funding-batch reference:{' '}
                                            <strong>{summary.matchedToFundingMonitor.toLocaleString()}</strong>
                                        </li>
                                        <li>
                                            Non-matched claims (before dedupe):{' '}
                                            <strong>{nonMatchedRaw.toLocaleString()}</strong>{' '}
                                            <span className={styles.scanTotalsMuted}>(scanned − matched)</span>
                                        </li>
                                        <li>
                                            Gap rows loaded (deduplicated): <strong>{summary.gapCount.toLocaleString()}</strong>
                                            {sorted.length !== summary.gapCount && (
                                                <span className={styles.scanTotalsMuted}>
                                                    {' '}
                                                    — UI table {sorted.length.toLocaleString()}
                                                </span>
                                            )}
                                        </li>
                                    </ul>
                                    <span className={styles.scanTotalsFooter}>
                                        The table lists deduped gap rows only; duplicated refs on separate lead rows collapse
                                        to one gap row.
                                    </span>
                                </span>
                            </span>
                        )}
                        <button type="button" className={styles.exportResultsBtn} disabled={exportDisabled} onClick={exportGapTableCsv}>
                            Export CSV
                        </button>
                    </div>
                }
            />
            {loading ? (
                <div className={styles.loading}>
                    <Spinner />
                </div>
            ) : (
                <>
            <p className={styles.lead}>
                Claims from <strong>PCP Sentinel</strong>, <strong>All Day Loans (LC)</strong>, <strong>PCPMO26</strong>, and{' '}
                <strong>MBF Check</strong> that do not match any reference already stored against a funding batch
                (<strong>active or audit-removed</strong>). This differs from{' '}
                <em>Ineligible funding clients</em>, which only lists audit removals.
            </p>

            {error && <p className={styles.error}>{error}</p>}

            {summary && !error && sorted.length > 0 && (
                <div className={efx.explorerToolbar}>
                    <div ref={gapFilterBarRef} className={efx.filterMenuBar} aria-label="Gap filters">
                        <div className={efx.filterMenuItem}>
                            <button
                                type="button"
                                className={`${efx.filterMenuPill} ${hasSearch ? efx.filterMenuPillActive : ''}`}
                                aria-expanded={openGapFilter === 'search'}
                                onClick={(e) => toggleGapFilter('search', e, 360)}
                            >
                                {hasSearch && (
                                    <span
                                        className={efx.filterMenuReset}
                                        aria-label="Clear search"
                                        onClick={(e) => {
                                            e.stopPropagation()
                                            setSearch('')
                                        }}
                                    />
                                )}
                                <span className={efx.filterMenuName}>Search</span>
                                {hasSearch && <span className={efx.filterMenuValue}>{searchSummary}</span>}
                                <span className={hasSearch ? efx.filterMenuEdit : efx.filterMenuPlus} aria-hidden />
                            </button>
                            {openGapFilter === 'search' && filterPopoverPosition && (
                                <div className={`${efx.filterPopover} ${efx.filterPopoverWide}`} style={filterPopoverPosition}>
                                    <span className={efx.filterSectionLabel}>Search gap rows</span>
                                    <input
                                        autoFocus
                                        className={styles.gapSearchInput}
                                        type="text"
                                        placeholder="Name, email, case ref, policy, introducer label…"
                                        value={search}
                                        onChange={(e) => setSearch(e.target.value)}
                                    />
                                </div>
                            )}
                        </div>

                        <div className={efx.filterMenuItem}>
                            <button
                                type="button"
                                className={`${efx.filterMenuPill} ${hasSourceFilter ? efx.filterMenuPillActive : ''}`}
                                aria-expanded={openGapFilter === 'source'}
                                onClick={(e) => toggleGapFilter('source', e, 430)}
                            >
                                {hasSourceFilter && (
                                    <span
                                        className={efx.filterMenuReset}
                                        aria-label="Reset source filter"
                                        onClick={(e) => {
                                            e.stopPropagation()
                                            resetSourceFilter()
                                        }}
                                    />
                                )}
                                <span className={efx.filterMenuName}>Introducer</span>
                                {hasSourceFilter && <span className={efx.filterMenuValue}>{sourceFilterSummary}</span>}
                                <span className={hasSourceFilter ? efx.filterMenuEdit : efx.filterMenuPlus} aria-hidden />
                            </button>
                            {openGapFilter === 'source' && filterPopoverPosition && (
                                <div className={`${efx.filterPopover} ${efx.filterPopoverWide}`} style={filterPopoverPosition}>
                                    <span className={efx.filterSectionLabel}>
                                        Introducer
                                        {hasSourceFilter && (
                                            <button type="button" className={efx.clearLink} onClick={() => resetSourceFilter()}>
                                                clear
                                            </button>
                                        )}
                                    </span>
                                    <div className={efx.cardWrap} role="group" aria-label="Filter by source database">
                                        {GAP_SOURCE_OPTIONS.map((name) => {
                                            const active = selectedSources.includes(name)
                                            const count = sourceCounts[name] ?? 0
                                            return (
                                                <button
                                                    key={name}
                                                    type="button"
                                                    title={name}
                                                    aria-pressed={active}
                                                    className={`${efx.filterCard} ${active ? efx.filterCardActive : ''}`}
                                                    onClick={() => toggleSource(name)}
                                                >
                                                    <span className={efx.filterCardLabel}>{name}</span>
                                                    <span className={efx.filterCardCount}>
                                                        {count.toLocaleString()} {count === 1 ? 'row' : 'rows'}
                                                    </span>
                                                </button>
                                            )
                                        })}
                                    </div>
                                </div>
                            )}
                        </div>

                        {lenderOptions.length > 0 && (
                            <div className={efx.filterMenuItem}>
                                <button
                                    type="button"
                                    className={`${efx.filterMenuPill} ${hasLenderFilter ? efx.filterMenuPillActive : ''}`}
                                    aria-expanded={openGapFilter === 'lender'}
                                    onClick={(e) => toggleGapFilter('lender', e, 480)}
                                >
                                    {hasLenderFilter && (
                                        <span
                                            className={efx.filterMenuReset}
                                            aria-label="Reset lender filter"
                                            onClick={(e) => {
                                                e.stopPropagation()
                                                resetLenderFilter()
                                            }}
                                        />
                                    )}
                                    <span className={efx.filterMenuName}>Lender</span>
                                    {hasLenderFilter && <span className={efx.filterMenuValue}>{lenderFilterSummary}</span>}
                                    <span className={hasLenderFilter ? efx.filterMenuEdit : efx.filterMenuPlus} aria-hidden />
                                </button>
                                {openGapFilter === 'lender' && filterPopoverPosition && (
                                    <div className={`${efx.filterPopover} ${efx.filterPopoverWide}`} style={filterPopoverPosition}>
                                        <span className={efx.filterSectionLabel}>
                                            Lender
                                            <span className={styles.lenderSortToggle}>
                                                <button
                                                    type="button"
                                                    className={`${styles.lenderSortBtn} ${lenderSort === 'count' ? styles.lenderSortBtnActive : ''}`}
                                                    onClick={() => setLenderSort('count')}
                                                >
                                                    Most rows
                                                </button>
                                                <button
                                                    type="button"
                                                    className={`${styles.lenderSortBtn} ${lenderSort === 'alpha' ? styles.lenderSortBtnActive : ''}`}
                                                    onClick={() => setLenderSort('alpha')}
                                                >
                                                    A–Z
                                                </button>
                                            </span>
                                            {hasLenderFilter && (
                                                <button type="button" className={efx.clearLink} onClick={() => resetLenderFilter()}>
                                                    clear
                                                </button>
                                            )}
                                        </span>
                                        <div className={efx.cardWrap} role="group" aria-label="Filter by lender">
                                            {lenderOptions.map((name) => {
                                                const active = selectedLenders.includes(name)
                                                const count = lenderCounts[name] ?? 0
                                                return (
                                                    <button
                                                        key={name}
                                                        type="button"
                                                        title={name}
                                                        aria-pressed={active}
                                                        className={`${efx.filterCard} ${active ? efx.filterCardActive : ''}`}
                                                        onClick={() => toggleLender(name)}
                                                    >
                                                        <span className={efx.filterCardLabel}>{name}</span>
                                                        <span className={efx.filterCardCount}>
                                                            {count.toLocaleString()} {count === 1 ? 'row' : 'rows'}
                                                        </span>
                                                    </button>
                                                )
                                            })}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* CFA signed date filter */}
                        <div className={efx.filterMenuItem}>
                            <button
                                type="button"
                                className={`${efx.filterMenuPill} ${hasCfaFilter ? efx.filterMenuPillActive : ''}`}
                                aria-expanded={openGapFilter === 'cfa'}
                                onClick={(e) => toggleGapFilter('cfa', e, 320)}
                            >
                                {hasCfaFilter && (
                                    <span
                                        className={efx.filterMenuReset}
                                        aria-label="Reset CFA date filter"
                                        onClick={(e) => {
                                            e.stopPropagation()
                                            resetCfaFilter()
                                        }}
                                    />
                                )}
                                <span className={efx.filterMenuName}>CFA date</span>
                                {hasCfaFilter && <span className={efx.filterMenuValue}>{cfaFilterSummary}</span>}
                                <span className={hasCfaFilter ? efx.filterMenuEdit : efx.filterMenuPlus} aria-hidden />
                            </button>
                            {openGapFilter === 'cfa' && filterPopoverPosition && (
                                <div className={efx.filterPopover} style={filterPopoverPosition}>
                                    <span className={efx.filterSectionLabel}>CFA signed date</span>
                                    <div className={efx.radioGroup}>
                                        {(['all', 'present', 'missing'] as const).map((opt) => (
                                            <label key={opt} className={efx.radioLabel}>
                                                <input
                                                    type="radio"
                                                    name="cfa"
                                                    checked={cfaFilter === opt}
                                                    onChange={() => setCfaFilter(opt)}
                                                />
                                                {opt === 'all' ? 'All' : opt === 'present' ? 'Has CFA date' : 'Missing CFA date'}
                                            </label>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* E-sign date filter */}
                        <div className={efx.filterMenuItem}>
                            <button
                                type="button"
                                className={`${efx.filterMenuPill} ${hasEsignFilter ? efx.filterMenuPillActive : ''}`}
                                aria-expanded={openGapFilter === 'esign'}
                                onClick={(e) => toggleGapFilter('esign', e, 320)}
                            >
                                {hasEsignFilter && (
                                    <span
                                        className={efx.filterMenuReset}
                                        aria-label="Reset e-sign date filter"
                                        onClick={(e) => {
                                            e.stopPropagation()
                                            resetEsignFilter()
                                        }}
                                    />
                                )}
                                <span className={efx.filterMenuName}>E-sign date</span>
                                {hasEsignFilter && <span className={efx.filterMenuValue}>{esignFilterSummary}</span>}
                                <span className={hasEsignFilter ? efx.filterMenuEdit : efx.filterMenuPlus} aria-hidden />
                            </button>
                            {openGapFilter === 'esign' && filterPopoverPosition && (
                                <div className={efx.filterPopover} style={filterPopoverPosition}>
                                    <span className={efx.filterSectionLabel}>E-sign date</span>
                                    <div className={efx.radioGroup}>
                                        {(['all', 'present', 'missing'] as const).map((opt) => (
                                            <label key={opt} className={efx.radioLabel}>
                                                <input
                                                    type="radio"
                                                    name="esign"
                                                    checked={esignFilter === opt}
                                                    onChange={() => setEsignFilter(opt)}
                                                />
                                                {opt === 'all' ? 'All' : opt === 'present' ? 'Has e-sign date' : 'Missing e-sign date'}
                                            </label>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Telephone filter */}
                        <div className={efx.filterMenuItem}>
                            <button
                                type="button"
                                className={`${efx.filterMenuPill} ${hasTelFilter ? efx.filterMenuPillActive : ''}`}
                                aria-expanded={openGapFilter === 'tel'}
                                onClick={(e) => toggleGapFilter('tel', e, 300)}
                            >
                                {hasTelFilter && (
                                    <span
                                        className={efx.filterMenuReset}
                                        aria-label="Reset telephone filter"
                                        onClick={(e) => { e.stopPropagation(); resetTelFilter() }}
                                    />
                                )}
                                <span className={efx.filterMenuName}>Telephone</span>
                                {hasTelFilter && <span className={efx.filterMenuValue}>{telFilterSummary}</span>}
                                <span className={hasTelFilter ? efx.filterMenuEdit : efx.filterMenuPlus} aria-hidden />
                            </button>
                            {openGapFilter === 'tel' && filterPopoverPosition && (
                                <div className={efx.filterPopover} style={filterPopoverPosition}>
                                    <span className={efx.filterSectionLabel}>Telephone</span>
                                    <div className={efx.radioGroup}>
                                        {(['all', 'present', 'missing'] as const).map((opt) => (
                                            <label key={opt} className={efx.radioLabel}>
                                                <input
                                                    type="radio"
                                                    name="tel"
                                                    checked={telFilter === opt}
                                                    onChange={() => setTelFilter(opt)}
                                                />
                                                {opt === 'all' ? 'All' : opt === 'present' ? 'Has telephone' : 'Missing telephone'}
                                            </label>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* DOB filter */}
                        <div className={efx.filterMenuItem}>
                            <button
                                type="button"
                                className={`${efx.filterMenuPill} ${hasDobFilter ? efx.filterMenuPillActive : ''}`}
                                aria-expanded={openGapFilter === 'dob'}
                                onClick={(e) => toggleGapFilter('dob', e, 300)}
                            >
                                {hasDobFilter && (
                                    <span
                                        className={efx.filterMenuReset}
                                        aria-label="Reset DOB filter"
                                        onClick={(e) => { e.stopPropagation(); resetDobFilter() }}
                                    />
                                )}
                                <span className={efx.filterMenuName}>DOB</span>
                                {hasDobFilter && <span className={efx.filterMenuValue}>{dobFilterSummary}</span>}
                                <span className={hasDobFilter ? efx.filterMenuEdit : efx.filterMenuPlus} aria-hidden />
                            </button>
                            {openGapFilter === 'dob' && filterPopoverPosition && (
                                <div className={efx.filterPopover} style={filterPopoverPosition}>
                                    <span className={efx.filterSectionLabel}>Date of birth</span>
                                    <div className={efx.radioGroup}>
                                        {(['all', 'present', 'missing'] as const).map((opt) => (
                                            <label key={opt} className={efx.radioLabel}>
                                                <input
                                                    type="radio"
                                                    name="dob"
                                                    checked={dobFilter === opt}
                                                    onChange={() => setDobFilter(opt)}
                                                />
                                                {opt === 'all' ? 'All' : opt === 'present' ? 'Has DOB' : 'Missing DOB'}
                                            </label>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Address filter */}
                        <div className={efx.filterMenuItem}>
                            <button
                                type="button"
                                className={`${efx.filterMenuPill} ${hasAddressFilter ? efx.filterMenuPillActive : ''}`}
                                aria-expanded={openGapFilter === 'address'}
                                onClick={(e) => toggleGapFilter('address', e, 300)}
                            >
                                {hasAddressFilter && (
                                    <span
                                        className={efx.filterMenuReset}
                                        aria-label="Reset address filter"
                                        onClick={(e) => { e.stopPropagation(); resetAddressFilter() }}
                                    />
                                )}
                                <span className={efx.filterMenuName}>Address</span>
                                {hasAddressFilter && <span className={efx.filterMenuValue}>{addressFilterSummary}</span>}
                                <span className={hasAddressFilter ? efx.filterMenuEdit : efx.filterMenuPlus} aria-hidden />
                            </button>
                            {openGapFilter === 'address' && filterPopoverPosition && (
                                <div className={efx.filterPopover} style={filterPopoverPosition}>
                                    <span className={efx.filterSectionLabel}>Address</span>
                                    <div className={efx.radioGroup}>
                                        {(['all', 'present', 'missing'] as const).map((opt) => (
                                            <label key={opt} className={efx.radioLabel}>
                                                <input
                                                    type="radio"
                                                    name="address"
                                                    checked={addressFilter === opt}
                                                    onChange={() => setAddressFilter(opt)}
                                                />
                                                {opt === 'all' ? 'All' : opt === 'present' ? 'Has address' : 'Missing address'}
                                            </label>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {summary && (
                <>
                    <div className={styles.metricCardsGrid}>
                        <IneligibleFundingClientsMetricCard
                            label="Introducer claims not in a batch"
                            value={filteredRows.length.toLocaleString()}
                            pct={gapPctOfTableRows}
                            variant="orange"
                            infoTooltipExtendRight
                            info={
                                `Introducer lead rows with no matching case ref or policy number on any funding-batch row ` +
                                `(including audit-removed). The count matches rows in the table after your search/source filters. ` +
                                `The percentage bar is that count divided by all ${sorted.length.toLocaleString()} gap rows loaded on this page—not the whole introducer scan.`
                            }
                        />
                        <IneligibleFundingClientsMetricCard
                            label="Unique clients"
                            value={gapDistinctEmails.toLocaleString()}
                            pct={distinctEmailsPctOfLoadedGap}
                            variant="purple"
                            info={
                                `Unique non-empty email addresses among rows in the table after your search/source filters. ` +
                                `The bar is that count divided by ${loadedGapDistinctEmails.toLocaleString()} unique emails ` +
                                `among all ${sorted.length.toLocaleString()} gap rows loaded on this page.`
                            }
                        />
                    </div>
                    <div className={styles.summary}>
                        <span>
                            Batch reference rows compared:{' '}
                            <strong>{summary.fundingReferenceRows.toLocaleString()}</strong>{' '}
                            (active + audit-removed)
                        </span>
                    </div>
                </>
            )}

            {sorted.length === 0 && !error ? (
                <p className={styles.empty}>Every scanned introducer claim matches at least one batch claim/reference.</p>
            ) : filteredRows.length === 0 ? (
                <p className={styles.empty}>No gap rows match the current filters.</p>
            ) : (
                <>
                    <p className={efx.tableNote}>
                        Showing{' '}
                        {(Math.min((currentPage - 1) * GAP_PAGE_SIZE + 1, filteredRows.length)).toLocaleString()}
                        {' '}–{' '}
                        {Math.min(currentPage * GAP_PAGE_SIZE, filteredRows.length).toLocaleString()}
                        {' '}of {filteredRows.length.toLocaleString()} gap rows
                        {filteredRows.length < sorted.length && ` (filtered from ${sorted.length.toLocaleString()})`}
                    </p>
                    <div className={efx.tableWrapper}>
                    <table className={`${efx.removedTable} ${styles.gapDataTable}`}>
                        <thead>
                            <tr>
                                <th>Source</th>
                                <th>Introducer</th>
                                <th>Case ref</th>
                                <th>Policy number</th>
                                <th>Title</th>
                                <th>First name</th>
                                <th>Last name</th>
                                <th>DOB</th>
                                <th>Email</th>
                                <th>Telephone</th>
                                <th>Address</th>
                                <th>Postcode</th>
                                <th>CCJs</th>
                                <th>Voluntary arrangements</th>
                                <th>Lender</th>
                                <th>Start date</th>
                                <th>Total amount payable</th>
                                <th>Starting balance</th>
                                <th>Contract (months)</th>
                                <th>Monthly repayment</th>
                                <th>CFA signed</th>
                                <th>E-sign date</th>
                            </tr>
                        </thead>
                        <tbody>
                            {paginatedRows.map((row) => {
                                const addressParts = [
                                    row.flat, row.houseNumber, row.houseName,
                                    row.addressLineOne, row.addressLineTwo,
                                    row.street, row.townCity, row.county,
                                ].filter(Boolean)
                                const address = addressParts.length > 0 ? addressParts.join(', ') : '—'
                                return (
                                <tr key={`${row.sourceDb}-${row.caseRef}-${row.policyNumber}`}>
                                    <td>{row.sourceDb}</td>
                                    <td>{row.introducerLabel || '—'}</td>
                                    <td>{row.caseRef || '—'}</td>
                                    <td>{row.policyNumber || '—'}</td>
                                    <td>{row.title || '—'}</td>
                                    <td>{row.firstName || '—'}</td>
                                    <td>{row.lastName || '—'}</td>
                                    <td>{row.dob ?? '—'}</td>
                                    <td>{row.email || '—'}</td>
                                    <td>{row.telephone || '—'}</td>
                                    <td>{address}</td>
                                    <td>{row.postcode || '—'}</td>
                                    <td>{row.ccjs != null ? row.ccjs : '—'}</td>
                                    <td>{row.voluntaryArrangements != null ? row.voluntaryArrangements : '—'}</td>
                                    <td>{row.lender || '—'}</td>
                                    <td>{row.startDate ?? '—'}</td>
                                    <td>{row.totalAmountPayable != null ? `£${Number(row.totalAmountPayable).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '—'}</td>
                                    <td>{row.startingBalance != null ? `£${Number(row.startingBalance).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '—'}</td>
                                    <td>{row.lengthOfContract ?? '—'}</td>
                                    <td>{row.monthlyRepayment != null ? `£${Number(row.monthlyRepayment).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '—'}</td>
                                    <td>{row.cfaSignedDate ?? '—'}</td>
                                    <td>{row.esignDate ?? '—'}</td>
                                </tr>
                                )
                            })}
                        </tbody>
                    </table>
                    </div>

                    {totalPages > 1 && (
                        <div className={styles.paginationSection}>
                            <div className={styles.paginationInfo}>
                                <span>Page {currentPage} of {totalPages}</span>
                                <span>
                                    Showing{' '}
                                    {((currentPage - 1) * GAP_PAGE_SIZE + 1).toLocaleString()}
                                    {' '}–{' '}
                                    {Math.min(currentPage * GAP_PAGE_SIZE, filteredRows.length).toLocaleString()}
                                    {' '}of {filteredRows.length.toLocaleString()} rows
                                </span>
                            </div>
                            <div className={styles.paginationControls}>
                                <button
                                    type="button"
                                    className={styles.paginationButton}
                                    onClick={() => handlePageChange(currentPage - 1)}
                                    disabled={currentPage === 1}
                                >
                                    Previous
                                </button>
                                {Array.from({ length: totalPages }, (_, i) => i + 1)
                                    .filter((page) => page === 1 || page === totalPages || (page >= currentPage - 2 && page <= currentPage + 2))
                                    .map((page, idx, arr) => (
                                        <div key={page} className={styles.paginationPages}>
                                            {idx > 0 && arr[idx - 1] !== page - 1 && (
                                                <span className={styles.paginationEllipsis}>...</span>
                                            )}
                                            <button
                                                type="button"
                                                className={`${styles.paginationButton} ${page === currentPage ? styles.active : ''}`}
                                                onClick={() => handlePageChange(page)}
                                            >
                                                {page}
                                            </button>
                                        </div>
                                    ))}
                                <button
                                    type="button"
                                    className={styles.paginationButton}
                                    onClick={() => handlePageChange(currentPage + 1)}
                                    disabled={currentPage === totalPages}
                                >
                                    Next
                                </button>
                            </div>
                        </div>
                    )}
                </>
            )}
                </>
            )}
        </div>
    )
}
